import type { Pixel } from "@pdfking/renderer-api"
import type {
  PDFReaderOptions,
  PDFSplitFunction,
  PDFSplitResult,
} from "@pdfking/renderer-api"
import { BoxToRectangle } from "@pdfking/renderer-api"
import type {
  PdfiumApi,
  PdfiumFunctions,
  PdfiumTypeMap,
  Pointer,
} from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { ToBlob } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { EnsurePage } from "../functions/EnsurePage"
import {
  ReadDocument,
  type ReadDocumentResult,
} from "../functions/ReadDocument"
import { PageRange } from "../utils/PageRange"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

type Box<T> = [T, T, T, T]

const get_page_box = async (
  { malloc, call, readFloats }: PdfiumApi,
  page: PointerPage,
  method: PdfiumFunctions
): Promise<Box<Pixel> | undefined> => {
  const allocs = [
    malloc(4 as Bytes),
    malloc(4 as Bytes),
    malloc(4 as Bytes),
    malloc(4 as Bytes),
  ] as const
  const pointers = allocs.map(a => a.pointer) as Box<Pointer>

  const success = await call(method, page, ...pointers)

  if (!success) {
    allocs.forEach(a => a.free())
    return
  }

  const points = pointers.map(p => readFloats(p, 1)[0])
  allocs.forEach(a => a.free())

  return points as Box<Pixel>
}

const extractPage = async (
  pdfium: PdfiumApi,
  reader: ReadDocumentResult,
  page: number
): Promise<PDFSplitResult> => {
  await EnsurePage(pdfium, reader, page)
  const { doc_pointer } = reader
  const {
    malloc,
    call,
    safeCall,
    readFloats,
    writeString,
    readBytes,
    addCallback,
    setInt32,
  } = pdfium

  const new_doc_pointer = await safeCall("FPDF_CreateNewDocument")

  const { pointer: pageRangePtr, free: freePageRange } = writeString(`${page}`)

  await safeCall(
    "FPDF_ImportPages",
    new_doc_pointer,
    doc_pointer,
    pageRangePtr,
    0
  )

  freePageRange()

  const imported_page = await safeCall("FPDF_LoadPage", new_doc_pointer, 0)

  const { pointer: size_pointer, free: freeSize } = malloc(8 as Bytes)
  await safeCall(
    "FPDF_GetPageSizeByIndexF",
    doc_pointer,
    page - 1,
    size_pointer
  )

  const [width, height] = readFloats(size_pointer, 2)
  const media_box = BoxToRectangle([0, 0, width, height] as Box<Pixel>)

  freeSize()

  const trim_box = await get_page_box(
    pdfium,
    imported_page,
    "FPDFPage_GetTrimBox"
  ).then(box => (box ? BoxToRectangle(box) : media_box))

  await call("FPDF_ClosePage", imported_page)

  const chunks: Uint8Array[] = []

  const write_callback = (_: number, data: number, size: number) => {
    chunks.push(readBytes(data as Pointer, size as Bytes))
    return size
  }

  const { pointer: callbackPtr, free: freeCallback } = addCallback(
    write_callback,
    "iiii"
  )
  const { pointer: structPtr, free: freeStruct } = malloc(8 as Bytes)

  setInt32(structPtr, 1)
  setInt32((structPtr + 4) as Pointer, callbackPtr)

  await safeCall("FPDF_SaveAsCopy", new_doc_pointer, structPtr, 2)

  freeStruct()
  freeCallback()
  await call("FPDF_CloseDocument", new_doc_pointer)

  return {
    blob: ToBlob(chunks, "application/pdf"),
    trim_box,
    media_box,
  }
}

async function* PDFSplitImpl(
  api: PdfiumApi,
  input: PDFReaderOptions,
  rangeFn?: (page_count: number) => Iterable<number>
): AsyncGenerator<PDFSplitResult> {
  const reader = await ReadDocument(api, input)

  try {
    const page_count = await api.call("FPDF_GetPageCount", reader.doc_pointer)

    for (const page of PageRange(page_count, rangeFn)) {
      yield await extractPage(api, reader, page)
    }
  } finally {
    await CloseDocument(api, reader)
  }
}

export const PDFSplit: PDFSplitFunction = (input, rangeFn) =>
  ProvisionPdfium(api => PDFSplitImpl(api, input, rangeFn))
